import { Link } from "react-router-dom";
import { MapPin, Bath, BedDouble, Ruler, Calendar, Heart } from "lucide-react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Property } from "@/types/types";
import { useFavorites } from "@/hooks/use-favorites";
import { cn } from "@/lib/utils";

interface PropertyCardProps {
  listing: Property;
  view?: "grid" | "list";
}

export default function PropertyCard({
  listing,
  view = "grid",
}: PropertyCardProps) {
  const isListView = view === "list";
  const { toggleFavorite, isFavorite } = useFavorites();
  const favorited = isFavorite(listing.id);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFavorite(listing.id);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (isListView) {
    return (
      <Card className="overflow-hidden hover:shadow-md transition-shadow">
        <div className="flex flex-col sm:flex-row">
          <div className="relative h-48 sm:h-auto sm:w-1/3">
            <img
              src={listing.image || "/placeholder.svg?height=400&width=600"}
              alt={listing.title}
              className="object-cover w-full h-full"
            />
            <div className="absolute top-2 left-2 flex gap-1">
              {listing.status === "vacant" && (
                <Badge className="bg-green-500">Available</Badge>
              )}
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/80 hover:bg-white"
              onClick={handleFavoriteClick}
            >
              <Heart
                className={cn(
                  "h-4 w-4 transition-colors",
                  favorited
                    ? "fill-red-500 text-red-500"
                    : "text-gray-600 hover:text-red-500"
                )}
              />
              <span className="sr-only">
                {favorited ? "Remove from favorites" : "Add to favorites"}
              </span>
            </Button>
          </div>

          <div className="flex-1 p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{listing.title}</h3>
                <div className="flex items-center text-muted-foreground mt-1">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span className="text-sm">{listing.address}</span>
                </div>
              </div>
              <div className="font-bold text-lg">
                {formatPrice(listing.rent)}/mo
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-4">
              <div className="flex items-center text-sm">
                <BedDouble className="h-4 w-4 mr-1" />
                <span>
                  {listing.bedrooms} {listing.bedrooms === 1 ? "Bed" : "Beds"}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <Bath className="h-4 w-4 mr-1" />
                <span>
                  {listing.bathrooms}{" "}
                  {listing.bathrooms === 1 ? "Bath" : "Baths"}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <Ruler className="h-4 w-4 mr-1" />
                <span>{listing.area} sqft</span>
              </div>
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-1" />
                <span>Listed on {formatDate(listing.createdAt)}</span>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <Badge variant="outline">{listing.type}</Badge>
              <Link to={`/properties/${listing.id}`}>
                <Button>View Details</Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative h-48 w-full overflow-hidden">
        <img
          src={listing.image || "/placeholder.svg?height=400&width=600"}
          alt={listing.title}
          className="object-cover transition-transform hover:scale-105"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          {listing.status === "vacant" && (
            <Badge className="bg-green-500">Available</Badge>
          )}
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/80 hover:bg-white"
          onClick={handleFavoriteClick}
        >
          <Heart
            className={cn(
              "h-4 w-4 transition-colors",
              favorited
                ? "fill-red-500 text-red-500"
                : "text-gray-600 hover:text-red-500"
            )}
          />
          <span className="sr-only">
            {favorited ? "Remove from favorites" : "Add to favorites"}
          </span>
        </Button>
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-lg line-clamp-1">{listing.title}</h3>
        <div className="flex items-center text-muted-foreground mt-1">
          <MapPin className="h-4 w-4 mr-1" />
          <span className="text-sm">{listing.address}</span>
        </div>
        <div className="flex justify-between mt-4">
          <div className="flex items-center text-sm">
            <BedDouble className="h-4 w-4 mr-1" />
            <span>
              {listing.bedrooms} {listing.bedrooms === 1 ? "Bed" : "Beds"}
            </span>
          </div>
          <div className="flex items-center text-sm">
            <Bath className="h-4 w-4 mr-1" />
            <span>
              {listing.bathrooms} {listing.bathrooms === 1 ? "Bath" : "Baths"}
            </span>
          </div>
          <div className="flex items-center text-sm">
            <Ruler className="h-4 w-4 mr-1" />
            <span>{listing.area} sqft</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex justify-between items-center">
        <div className="font-bold text-lg">{formatPrice(listing.rent)}/mo</div>
        <Link to={`/properties/${listing.id}`}>
          <Button variant="outline" size="sm">
            View Details
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
